import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Assistant, QiaIcon, QiaWordmark } from "@qitech/dash-agent-sdk";
import { useTranslation } from "react-i18next";
import { requestChatGrant } from "./agentSession";
import "./AgentChat.css";

const GATEWAY_BASE_URL = process.env.REACT_APP_DASH_AGENT_GATEWAY_URL;

const DEFAULT_PANEL_WIDTH = 380;
const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 700;

function AgentChat({
    chatContext,
    turnContext,
    renderers,
    attachments,
    onAttachmentsChange,
    open,
    onPanelOpenChange,
    alwaysNewChat,
    title,
    greeting,
    emptySubtitle,
    suggestions
}) {
    const { t } = useTranslation();

    const [reservedWidth, setReservedWidth] = useState(0);
    const [animateReflow, setAnimateReflow] = useState(true);

    const [queueTurnContext, setQueueTurnContext] = useState(null);
    const [queueChatContext, setQueueChatContext] = useState(null);

    const handleContextSender = useCallback((sender) => setQueueTurnContext(() => sender), []);
    const handleChatContextSender = useCallback((sender) => setQueueChatContext(() => sender), []);

    const turnContextJson = useMemo(
        () => (turnContext && turnContext.length > 0 ? JSON.stringify(turnContext) : null),
        [turnContext]
    );
    const chatContextJson = useMemo(
        () => (chatContext && chatContext.length > 0 ? JSON.stringify(chatContext) : null),
        [chatContext]
    );

    useEffect(() => {
        if (!queueTurnContext || !turnContextJson) return;
        queueTurnContext(JSON.parse(turnContextJson));
    }, [queueTurnContext, turnContextJson]);

    useEffect(() => {
        if (!queueChatContext || !chatContextJson) return;
        queueChatContext(JSON.parse(chatContextJson));
    }, [queueChatContext, chatContextJson]);

    const handleWidthChange = useCallback((width, options) => {
        setReservedWidth(width);
        setAnimateReflow(!options || options.animate !== false);
    }, []);

    const hostOwnsPanel = typeof open === "boolean";

    if (!GATEWAY_BASE_URL) return null;

    return (
        <React.Fragment>
            <div
                style={{
                    width: reservedWidth + "px",
                    flexShrink: 0,
                    transition: animateReflow ? "width .2s ease" : "none"
                }}
            />
            <Assistant
                gatewayBaseUrl={GATEWAY_BASE_URL}
                getChatGrant={requestChatGrant}
                title={title || t("Assistente")}
                greeting={greeting}
                emptySubtitle={emptySubtitle}
                suggestions={suggestions}
                displayMode="push"
                side="right"
                defaultWidth={DEFAULT_PANEL_WIDTH}
                minWidth={MIN_PANEL_WIDTH}
                maxWidth={MAX_PANEL_WIDTH}
                resizable
                onWidthChange={handleWidthChange}
                renderers={renderers}
                attachments={attachments}
                onAttachmentsChange={onAttachmentsChange}
                open={hostOwnsPanel ? open : undefined}
                hideLauncher={hostOwnsPanel}
                onOpenChange={onPanelOpenChange}
                alwaysNewChat={alwaysNewChat}
                onContextSender={handleContextSender}
                onChatContextSender={handleChatContextSender}
            />
        </React.Fragment>
    );
}

export function AgentChatLauncher({ open, onOpenChange, title }) {
    const { t } = useTranslation();

    if (!GATEWAY_BASE_URL || open) return null;

    return (
        <button
            type="button"
            className="qia-launcher"
            onClick={() => onOpenChange(true)}
            aria-label={title || t("Assistente")}
        >
            <QiaIcon size={22}/>
            <span className="qia-launcher-word"><QiaWordmark size={14}/></span>
        </button>
    );
}

export default AgentChat;
